import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Scissors, X, Play, Pause, SkipBack, SkipForward,
  Share2, Copy, Check, Heart, Clock, ChevronDown, ChevronUp, Lock, Crown, User, Search
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ClipCreator - Lets viewers create highlight clips from a game VOD.
 * Appears on the GameWatchLive page.
 * Requires Plus or Elite subscription to create clips.
 */
export default function ClipCreator({ gameId, videoRef, gameName, compact = false, yellowStyle = false }) {
  const { token, hasRequiredTier, isAuthenticated, getSubscriptionTier } = useAuth();
  const navigate = useNavigate();
  
  // Any logged-in user can create clips (Share-a-Clip Engine, P0 — Feb 22, 2026)
  const canCreateClips = isAuthenticated();
  const [open, setOpen] = useState(false);
  const [startTime, setStartTime] = useState(0);
  const [endTime, setEndTime] = useState(0);
  const [startTimeText, setStartTimeText] = useState('');
  const [endTimeText, setEndTimeText] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [clips, setClips] = useState([]);
  const [showClips, setShowClips] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  
  // Player tagging
  const [playerName, setPlayerName] = useState('');
  const [athletes, setAthletes] = useState([]);
  const [showAthleteDropdown, setShowAthleteDropdown] = useState(false);
  const [athleteSearch, setAthleteSearch] = useState('');

  useEffect(() => {
    fetchClips();
  }, [gameId]);

  useEffect(() => {
    // Fetch athletes when clip creator opens
    if (open && athletes.length === 0) {
      fetchAthletes();
    }
  }, [open]);

  const fetchAthletes = async () => {
    try {
      const res = await fetch(`${API_URL}/api/athletes?limit=50`);
      if (res.ok) {
        const data = await res.json();
        setAthletes(data.filter(a => !a.name?.toLowerCase().startsWith('test')));
      }
    } catch (err) {
      console.error('Error fetching athletes:', err);
    }
  };

  const fetchClips = async () => {
    try {
      const res = await fetch(`${API_URL}/api/vod/clips?game_id=${gameId}`);
      if (res.ok) {
        const data = await res.json();
        setClips(data);
      }
    } catch (err) {
      console.error('Error fetching clips:', err);
    }
  };

  // Check if videoRef can provide meaningful time (non-zero means HLS native player is active)
  const canReadVideoTime = () => {
    try {
      const t = videoRef?.current?.currentTime;
      const d = videoRef?.current?.duration;
      return typeof t === 'number' && (t > 0 || (typeof d === 'number' && d > 0));
    } catch { return false; }
  };

  const updateStartTime = (seconds) => {
    setStartTime(seconds);
    setStartTimeText(formatTime(seconds));
  };

  const updateEndTime = (seconds) => {
    setEndTime(seconds);
    setEndTimeText(formatTime(seconds));
  };

  const markStart = () => {
    if (canReadVideoTime()) {
      const t = Math.floor(videoRef.current.currentTime);
      updateStartTime(t);
      toast.success(`Start: ${formatTime(t)}`);
    } else {
      toast.info('Enter start time manually (m:ss format)');
    }
  };

  const markEnd = () => {
    if (canReadVideoTime()) {
      const t = Math.ceil(videoRef.current.currentTime);
      updateEndTime(t);
      toast.success(`End: ${formatTime(t)}`);
    } else {
      toast.info('Enter end time manually (m:ss format)');
    }
  };

  const previewClip = () => {
    if (videoRef?.current && startTime > 0) {
      videoRef.current.currentTime = startTime;
      videoRef.current.play();
    }
  };

  // Parse "m:ss" or raw seconds input into total seconds
  const parseTimeInput = (val) => {
    if (!val) return 0;
    if (val.includes(':')) {
      const parts = val.split(':');
      const mins = parseInt(parts[0]) || 0;
      const secs = parseInt(parts[1]) || 0;
      return mins * 60 + secs;
    }
    return parseInt(val) || 0;
  };

  const commitStartTime = () => {
    const parsed = parseTimeInput(startTimeText);
    setStartTime(parsed);
    setStartTimeText(formatTime(parsed));
  };

  const commitEndTime = () => {
    const parsed = parseTimeInput(endTimeText);
    setEndTime(parsed);
    setEndTimeText(formatTime(parsed));
  };

  const createClip = async () => {
    if (!title.trim()) {
      toast.error('Give your clip a title');
      return;
    }
    if (endTime <= startTime) {
      toast.error('End time must be after start time');
      return;
    }
    if (endTime - startTime < 5) {
      toast.error('Clips must be at least 5 seconds');
      return;
    }
    if (endTime - startTime > 30) {
      toast.error('Clips can be max 30 seconds');
      return;
    }

    setCreating(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const res = await fetch(`${API_URL}/api/vod/clips`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          game_id: gameId,
          title: title.trim(),
          description: description.trim() || null,
          start_time: startTime,
          end_time: endTime,
          player_name: playerName.trim() || null,
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success('Clip created! Watermark rendering…');
        setTitle('');
        setDescription('');
        setPlayerName('');
        setStartTime(0);
        setEndTime(0);
        setStartTimeText('');
        setEndTimeText('');
        setOpen(false);
        fetchClips();
        // Take the user straight to the shareable clip page
        const newClipId = data?.clip?.clip_id;
        if (newClipId) {
          navigate(`/clip/${newClipId}`);
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create clip');
      }
    } catch (err) {
      toast.error('Error creating clip');
    } finally {
      setCreating(false);
    }
  };

  const shareClip = async (clip) => {
    const url = `${window.location.origin}/clip/${clip.clip_id}`;
    
    // Track share
    fetch(`${API_URL}/api/vod/clips/${clip.clip_id}/share`, { method: 'POST' }).catch(() => {});

    if (navigator.share) {
      try {
        await navigator.share({
          title: clip.title,
          text: `${clip.title} - ${clip.home_team} vs ${clip.away_team}`,
          url
        });
        return;
      } catch (e) {
        if (e.name === 'AbortError') return;
      }
    }

    navigator.clipboard.writeText(url);
    setCopiedId(clip.clip_id);
    toast.success('Link copied!');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const likeClip = async (clipId) => {
    try {
      await fetch(`${API_URL}/api/vod/clips/${clipId}/like`, { method: 'POST' });
      setClips(prev => prev.map(c => c.clip_id === clipId ? { ...c, likes: (c.likes || 0) + 1 } : c));
    } catch (err) {}
  };

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  // Handle clip button click. Any signed-in user can clip; guests get prompted to sign in.
  const handleClipButtonClick = () => {
    if (!isAuthenticated()) {
      toast.error('Please sign in to create clips');
      navigate('/login');
      return;
    }
    setOpen(!open);
  };

  return (
    <div className={compact ? "" : "space-y-2"} data-testid="clip-creator">
      {/* Upgrade Prompt Modal for Basic Users */}
      {showUpgradePrompt && (
        <>
          <div 
            className="fixed inset-0 bg-black/70 z-50" 
            onClick={() => setShowUpgradePrompt(false)}
          />
          <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:w-full sm:max-w-md z-50 bg-zinc-900 border border-zinc-700 rounded-xl p-6 text-center" data-testid="upgrade-prompt-modal">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Crown className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Upgrade to Plus</h3>
            <p className="text-zinc-400 text-sm mb-6">
              Clip creation is a premium feature available to Plus and Elite members. 
              Create highlight clips up to 20 seconds, share with friends, and build your collection.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => setShowUpgradePrompt(false)}
                variant="outline"
                className="flex-1 border-zinc-700 text-zinc-400 hover:text-white"
                data-testid="upgrade-prompt-cancel"
              >
                Maybe Later
              </Button>
              <Button
                onClick={() => {
                  setShowUpgradePrompt(false);
                  navigate('/membership');
                }}
                className="flex-1 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold"
                data-testid="upgrade-prompt-cta"
              >
                View Plans
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Clip Button */}
      <div className={compact ? "" : "flex items-center gap-2"}>
        <Button
          onClick={handleClipButtonClick}
          variant={yellowStyle ? "default" : (open ? "default" : "ghost")}
          size="sm"
          className={`${
            yellowStyle 
              ? "bg-rooam-blue hover:bg-rooam-blue-light text-black font-semibold h-9 px-4 rounded-lg shadow-sm"
              : open 
                ? "bg-red-600 hover:bg-red-500 text-white" 
                : compact 
                  ? "text-zinc-400 hover:text-white h-8 w-8 p-0 sm:w-auto sm:px-3"
                  : "border-zinc-700 text-zinc-300 hover:text-white hover:border-zinc-500"
          } ${!canCreateClips && !yellowStyle ? "opacity-75" : ""}`}
          data-testid="create-clip-btn"
        >
          {!canCreateClips && !yellowStyle ? (
            <Lock className={`w-4 h-4 ${compact ? "sm:mr-1.5" : "mr-1.5"}`} />
          ) : (
            <Scissors className={`w-4 h-4 ${yellowStyle || !compact ? "mr-1.5" : "sm:mr-1.5"}`} />
          )}
          {yellowStyle ? (
            <span>Clip</span>
          ) : compact ? (
            <span className="hidden sm:inline">{open ? 'Clip...' : 'Clip'}</span>
          ) : (
            open ? 'Creating Clip...' : (!canCreateClips ? 'Sign in to Clip' : 'Create Clip')
          )}
        </Button>

        {!compact && clips.length > 0 && (
          <button
            onClick={() => setShowClips(!showClips)}
            className="flex items-center gap-1 text-xs text-zinc-500 hover:text-white transition-colors"
            data-testid="toggle-clips-list"
          >
            <Badge className="bg-zinc-800 text-zinc-400">{clips.length}</Badge>
            Clips
            {showClips ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        )}
      </div>

      {/* Clip Creator Panel - Bottom sheet on mobile, inline on desktop */}
      {open && canCreateClips && (
        <>
          {/* Mobile backdrop */}
          <div 
            className="fixed inset-0 bg-black/50 z-40 sm:hidden" 
            onClick={() => setOpen(false)}
          />
          <div 
            className="fixed sm:relative inset-x-0 bottom-0 sm:inset-auto z-50 sm:z-auto bg-zinc-900 border border-zinc-700 sm:rounded-xl rounded-t-xl p-4 space-y-3 max-h-[70vh] overflow-y-auto" 
            data-testid="clip-creator-panel"
          >
            {/* Drag handle for mobile */}
            <div className="sm:hidden flex justify-center mb-2">
              <div className="w-10 h-1 bg-zinc-600 rounded-full" />
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-white">New Highlight Clip</span>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-zinc-800 rounded">
                <X className="w-4 h-4 text-zinc-400" />
              </button>
            </div>

            {/* Time Range */}
            <div className="space-y-2">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
                <Button onClick={markStart} size="sm" variant="outline" className="border-emerald-700 text-emerald-400 hover:bg-emerald-900/30 flex-1" data-testid="mark-start-btn">
                  <SkipBack className="w-3 h-3 mr-1" /> Mark Start
                </Button>
                <div className="text-center py-2 sm:py-0 shrink-0">
                  <div className="text-xs text-zinc-500">Duration</div>
                  <div className="text-sm font-mono text-white">
                    {formatTime(startTime)} → {formatTime(endTime)}
                  </div>
                  {endTime > startTime && (
                    <div className="text-[10px] text-cyan-400">{formatTime(endTime - startTime)}</div>
                  )}
                </div>
                <Button onClick={markEnd} size="sm" variant="outline" className="border-red-700 text-red-400 hover:bg-red-900/30 flex-1" data-testid="mark-end-btn">
                  Mark End <SkipForward className="w-3 h-3 ml-1" />
                </Button>
              </div>
              
              {/* Manual time inputs - always shown as fallback */}
              <div className="flex items-center gap-2 bg-zinc-800/60 rounded-lg px-3 py-2">
                <Clock className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
                <span className="text-xs text-zinc-500 shrink-0">Set times:</span>
                <input
                  type="text"
                  value={startTimeText}
                  onChange={e => setStartTimeText(e.target.value)}
                  onBlur={commitStartTime}
                  onKeyDown={e => e.key === 'Enter' && commitStartTime()}
                  placeholder="0:00"
                  className="w-14 bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-xs text-emerald-400 text-center font-mono focus:outline-none focus:border-emerald-600"
                  data-testid="clip-start-time-input"
                />
                <span className="text-xs text-zinc-500">→</span>
                <input
                  type="text"
                  value={endTimeText}
                  onChange={e => setEndTimeText(e.target.value)}
                  onBlur={commitEndTime}
                  onKeyDown={e => e.key === 'Enter' && commitEndTime()}
                  placeholder="0:00"
                  className="w-14 bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-xs text-red-400 text-center font-mono focus:outline-none focus:border-red-600"
                  data-testid="clip-end-time-input"
                />
                {endTime > startTime && (
                  <span className="text-[10px] text-cyan-400 ml-1">{endTime - startTime}s</span>
                )}
              </div>
            </div>

          {/* Preview - only show when videoRef is functional */}
          {startTime > 0 && canReadVideoTime() && (
            <Button onClick={previewClip} size="sm" variant="outline" className="w-full border-zinc-700 text-zinc-300" data-testid="preview-clip-btn">
              <Play className="w-3 h-3 mr-1" /> Preview from {formatTime(startTime)}
            </Button>
          )}

          {/* Title */}
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Clip title (e.g., 'Amazing 3-pointer')"
            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-cyan-600"
            maxLength={100}
            data-testid="clip-title-input"
          />

          {/* Description (optional) */}
          <input
            type="text"
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Description (optional)"
            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-cyan-600"
            maxLength={200}
            data-testid="clip-desc-input"
          />

          {/* Tag Player */}
          <div className="relative">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input
                  type="text"
                  value={playerName}
                  onChange={e => {
                    setPlayerName(e.target.value);
                    setAthleteSearch(e.target.value);
                    setShowAthleteDropdown(true);
                  }}
                  onFocus={() => setShowAthleteDropdown(true)}
                  placeholder="Tag player (optional)"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-cyan-600"
                  maxLength={100}
                  data-testid="clip-player-input"
                />
              </div>
              {playerName && (
                <button 
                  onClick={() => { setPlayerName(''); setAthleteSearch(''); }}
                  className="p-2 text-zinc-400 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            
            {/* Athlete Dropdown */}
            {showAthleteDropdown && athletes.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-zinc-800 border border-zinc-700 rounded-lg max-h-32 overflow-y-auto shadow-xl">
                {athletes
                  .filter(a => !athleteSearch || a.name?.toLowerCase().includes(athleteSearch.toLowerCase()))
                  .slice(0, 5)
                  .map(athlete => (
                    <button
                      key={athlete.id}
                      onClick={() => {
                        setPlayerName(athlete.name);
                        setShowAthleteDropdown(false);
                      }}
                      className="w-full px-3 py-2 text-left text-sm text-zinc-300 hover:bg-zinc-700 flex items-center gap-2"
                      data-testid={`select-athlete-${athlete.id}`}
                    >
                      <img 
                        src={athlete.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${athlete.id}`}
                        alt=""
                        className="w-5 h-5 rounded-full"
                      />
                      <span>{athlete.name}</span>
                      <span className="text-xs text-zinc-500">#{athlete.number} • {athlete.position}</span>
                    </button>
                  ))
                }
                {athletes.filter(a => !athleteSearch || a.name?.toLowerCase().includes(athleteSearch.toLowerCase())).length === 0 && (
                  <div className="px-3 py-2 text-sm text-zinc-500">No athletes found</div>
                )}
              </div>
            )}
          </div>

          {/* Create Button */}
          <Button
            onClick={createClip}
            disabled={creating || !title.trim() || endTime <= startTime}
            className="w-full bg-red-600 hover:bg-red-500 text-white font-semibold h-10"
            data-testid="save-clip-btn"
          >
            {creating ? 'Creating...' : 'Save Clip'}
          </Button>
        </div>
        </>
      )}

      {/* Clips List */}
      {showClips && clips.length > 0 && (
        <div className="bg-zinc-900/80 border border-zinc-800 rounded-xl overflow-hidden" data-testid="clips-list">
          <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between">
            <span className="text-xs font-semibold text-zinc-400 uppercase">Highlights ({clips.length})</span>
          </div>
          <div className="max-h-64 overflow-y-auto divide-y divide-zinc-800/50">
            {clips.map(clip => (
              <div key={clip.clip_id} className="px-3 py-2.5 hover:bg-zinc-800/50 transition-colors">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <button
                      onClick={() => {
                        if (videoRef?.current) {
                          videoRef.current.currentTime = clip.start_time;
                          videoRef.current.play();
                        }
                      }}
                      className="text-sm font-medium text-white hover:text-cyan-400 transition-colors truncate block w-full text-left"
                      data-testid={`play-clip-${clip.clip_id}`}
                    >
                      <Play className="w-3 h-3 inline mr-1 text-red-500" />
                      {clip.title}
                    </button>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] text-zinc-600 font-mono">
                        <Clock className="w-2.5 h-2.5 inline mr-0.5" />
                        {formatTime(clip.start_time)} - {formatTime(clip.end_time)} ({clip.duration}s)
                      </span>
                      {clip.player_name && (
                        <span className="text-[10px] text-cyan-500">
                          <User className="w-2.5 h-2.5 inline mr-0.5" />{clip.player_name}
                        </span>
                      )}
                      {clip.views > 0 && (
                        <span className="text-[10px] text-zinc-600">{clip.views} views</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => likeClip(clip.clip_id)}
                      className="p-1.5 rounded hover:bg-zinc-700 transition-colors"
                      data-testid={`like-clip-${clip.clip_id}`}
                    >
                      <Heart className={`w-3.5 h-3.5 ${clip.likes > 0 ? 'text-red-500 fill-red-500' : 'text-zinc-500'}`} />
                    </button>
                    <button
                      onClick={() => shareClip(clip)}
                      className="p-1.5 rounded hover:bg-zinc-700 transition-colors"
                      data-testid={`share-clip-${clip.clip_id}`}
                    >
                      {copiedId === clip.clip_id ? (
                        <Check className="w-3.5 h-3.5 text-green-500" />
                      ) : (
                        <Share2 className="w-3.5 h-3.5 text-zinc-500" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
